import { TranslationResult } from '../shared/types';

// Import the slang dictionary data
import slangData from '../data/slang_dictionary.json';

interface SlangDictionary {
  entries: Record<string, string>;
  metadata: {
    version: string;
    last_updated: string;
    total_entries: number;
    categories: string[];
    description: string;
  };
}

class ClientTranslationService {
  private dictionary: SlangDictionary;

  constructor() {
    this.dictionary = slangData as SlangDictionary;
  }

  /**
   * Translate a Mumbai slang phrase to English
   */
  async translate(phrase: string): Promise<TranslationResult> {
    const normalizedPhrase = phrase.toLowerCase().trim();
    
    // Direct match
    const directMatch = this.dictionary.entries[normalizedPhrase];
    if (directMatch) {
      return {
        originalPhrase: phrase,
        translation: directMatch,
        confidence: 1.0,
        culturalContext: this.getCulturalContext(normalizedPhrase),
        category: this.getCategory(normalizedPhrase),
        examples: this.getExamples(normalizedPhrase)
      };
    }

    // Fuzzy matching for partial matches
    const fuzzyMatches = this.findFuzzyMatches(normalizedPhrase);
    if (fuzzyMatches.length > 0) {
      const bestMatch = fuzzyMatches[0];
      return {
        originalPhrase: phrase,
        translation: this.dictionary.entries[bestMatch.key],
        confidence: bestMatch.score,
        culturalContext: this.getCulturalContext(bestMatch.key),
        category: this.getCategory(bestMatch.key),
        examples: this.getExamples(bestMatch.key),
        suggestions: fuzzyMatches.slice(0, 3).map(m => m.key)
      };
    }

    // No match found
    const suggestions = await this.getSuggestions(normalizedPhrase);
    return {
      originalPhrase: phrase,
      translation: "Translation not found",
      confidence: 0,
      culturalContext: "This phrase is not in our Mumbai slang dictionary. Try searching for individual words or check the spelling.",
      category: "unknown",
      examples: [],
      suggestions: suggestions.slice(0, 5)
    };
  }

  /**
   * Get suggestions based on partial input
   */
  async getSuggestions(query: string): Promise<string[]> {
    const normalizedQuery = query.toLowerCase().trim();
    
    if (normalizedQuery.length < 2) {
      return [];
    }

    const suggestions: string[] = [];
    
    // Find entries that start with the query
    Object.keys(this.dictionary.entries).forEach(key => {
      if (key.startsWith(normalizedQuery)) {
        suggestions.push(key);
      }
    });

    // Find entries that contain the query
    Object.keys(this.dictionary.entries).forEach(key => {
      if (key.includes(normalizedQuery) && !suggestions.includes(key)) {
        suggestions.push(key);
      }
    });

    return suggestions.slice(0, 10);
  }

  /**
   * Find fuzzy matches using simple string similarity
   */
  private findFuzzyMatches(phrase: string): Array<{ key: string; score: number }> {
    const matches: Array<{ key: string; score: number }> = [];

    Object.keys(this.dictionary.entries).forEach(key => {
      const score = this.calculateSimilarity(phrase, key);
      if (score > 0.6) { // Only include matches with >60% similarity
        matches.push({ key, score });
      }
    });

    return matches.sort((a, b) => b.score - a.score);
  }

  /**
   * Calculate string similarity (simple implementation)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Get cultural context for a phrase
   */
  private getCulturalContext(phrase: string): string {
    const contextMap: Record<string, string> = {
      'vada pav': 'Mumbai\'s most iconic street food, often called the "burger of Mumbai". A must-try for any visitor!',
      'local': 'The lifeline of Mumbai - suburban trains that connect the entire city. Peak hours can be extremely crowded.',
      'cutting': 'A uniquely Mumbai way to order tea - half a cup, perfect for sharing or when you want just a sip.',
      'tapri': 'Small roadside tea stalls found on every Mumbai street corner, perfect for quick chai and gossip.',
      'bhidu': 'Classic Mumbai tapori (street-smart) way to address a friend. Made famous by Bollywood movies.',
      'bindaas': 'Embodies the Mumbai spirit - being carefree and confident despite life\'s challenges.',
      'jugaad': 'The Mumbai way of finding creative solutions to problems with limited resources.',
      'mumbai spirit': 'The resilient, never-give-up attitude that Mumbaikars are famous for.',
      'apna time aayega': 'Popular phrase meaning "our time will come" - reflects Mumbai\'s optimistic spirit.',
      'sobo': 'South Mumbai - the posh area known for its colonial architecture and upscale lifestyle.',
      'virar fast': 'The most crowded train route in Mumbai, a true test of survival skills!'
    };

    return contextMap[phrase] || `This is a common Mumbai expression used in daily conversation by locals.`;
  }

  /**
   * Get category for a phrase
   */
  private getCategory(phrase: string): string {
    const categoryMap: Record<string, string> = {
      // Food culture
      'vada pav': 'food_culture', 'cutting': 'food_culture', 'tapri': 'food_culture',
      'bhel': 'food_culture', 'pani puri': 'food_culture', 'pau bhaji': 'food_culture',
      
      // Transport
      'local': 'local_travel', 'fast': 'local_travel', 'virar fast': 'local_travel',
      'slow': 'local_travel', 'footboard': 'local_travel',
      
      // Tapori/Street
      'bhidu': 'tapori_street', 'bindaas': 'tapori_street', 'tapori': 'tapori_street',
      'jugaad': 'tapori_street', 'chalu': 'tapori_street',
      
      // Everyday
      'bhai': 'everyday', 'scene': 'everyday', 'mast': 'everyday', 'solid': 'everyday',
      
      // Corporate/Modern
      'work ka scene': 'corporate_modern', 'wfh scene': 'corporate_modern',
      'bandwidth nahi': 'corporate_modern', 'loop me daal': 'corporate_modern',
      
      // Culture/Identity
      'mumbai spirit': 'culture_identity', 'apna time aayega': 'culture_identity',
      'sobo': 'culture_identity', 'mumbai meri jaan': 'culture_identity'
    };

    return categoryMap[phrase] || 'everyday';
  }

  /**
   * Get usage examples for a phrase
   */
  private getExamples(phrase: string): string[] {
    const exampleMap: Record<string, string[]> = {
      'bhai': [
        'Bhai, kya scene hai? (Brother, what\'s the plan?)',
        'Bhai sahab, help kar na! (Brother, please help!)'
      ],
      'scene': [
        'Kya scene hai aaj? (What\'s the plan today?)',
        'Movie ka scene hai kya? (Are we watching a movie?)'
      ],
      'mast': [
        'Yeh chai mast hai! (This tea is awesome!)',
        'Mast weather hai aaj! (Great weather today!)'
      ],
      'local': [
        'Local pakad ke office jaunga (I\'ll catch the local train to office)',
        'Local mein bahut rush hai (The local train is very crowded)'
      ],
      'cutting': [
        'Ek cutting chai dena (Give me half a cup of tea)',
        'Cutting peeke jaate hai (Let\'s have some tea and go)'
      ],
      'bindaas': [
        'Bindaas jao, tension mat lo (Go carefree, don\'t worry)',
        'Woh bindaas ladka hai (He\'s a carefree guy)'
      ]
    };

    return exampleMap[phrase] || [
      `"${phrase}" is commonly used in Mumbai conversations`,
      `You might hear "${phrase}" in local trains or street conversations`
    ];
  }
}

// Export singleton instance
export const clientTranslationService = new ClientTranslationService();